import React, { useState, useContext } from "react";
import ThemeContext from "../lib/theme-context";
import AuthContext from "../lib/auth-context";
import { 
  getBacktestAccount, 
  updateBacktestBalance
} from "../utils/backtestAccountUtils";
import throwMessage from "../utils/throwMessage";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CustomSingleTextField from "./textfields/singleRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

import EditIcon from "@mui/icons-material/Edit";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";

const BacktestAccountCard = () => {
  const theme = useContext(ThemeContext);
  const auth = useContext(AuthContext);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [balance, setBalance] = useState(getBacktestAccount().currentBalance);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleBalanceUpdate = async () => {
    if (isNaN(balance) || balance <= 0) {
      throwMessage("warning", "Please enter a valid positive balance");
      return;
    }

    setIsUpdating(true);
    try {
      updateBacktestBalance(balance, "Manual balance adjustment");
      
      // Trigger recalculation if in backtest mode
      if (auth.backtestingMode && auth.calculateFields) {
        setTimeout(() => {
          if (auth.trades && auth.trades.length > 0) {
            auth.calculateFields(auth.trades, true, localStorage.getItem("backtest-active"));
          }
        }, 100);
      }
      
      throwMessage("success", `Backtest balance updated to $${balance.toFixed(2)}`);
      setOpenEditDialog(false);
    } catch (error) {
      throwMessage("error", "Failed to update balance");
    } finally {
      setIsUpdating(false);
    }
  };

  const backtestAccount = getBacktestAccount();

  return (
    <>
      <Card
        sx={{
          bgcolor: theme[0] === "dark" ? "#25262b" : "#ffffff",
          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
          borderRadius: "12px",
          border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
          boxShadow: theme[0] === "dark" 
            ? "0 4px 12px rgba(0, 0, 0, 0.3)"
            : "0 4px 12px rgba(0, 0, 0, 0.08)",
          transition: "all 0.2s ease",
          minHeight: "280px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "&:hover": {
            boxShadow: theme[0] === "dark" 
              ? "0 8px 24px rgba(0, 0, 0, 0.5)"
              : "0 8px 24px rgba(0, 0, 0, 0.12)",
            transform: "translateY(-2px)"
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 }, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <SavingsOutlinedIcon sx={{ fontSize: "24px", color: "#3b82f6" }} />
            <Typography variant="h6" sx={{ ml: 1.5, fontSize: { xs: "18px", sm: "20px" }, fontWeight: "600" }}>
              Backtesting
            </Typography>
          </Box>
          
          <Chip
            label="Backtesting"
            size="small"
            sx={{
              mb: 2,
              backgroundColor: "#3b82f6",
              color: "#fff",
              fontSize: "13px",
              height: "28px",
              fontWeight: "500",
              px: 1
            }}
          />

          <Typography 
            variant="body2" 
            sx={{ 
              color: theme[0] === "dark" ? "#4CAF50" : "#2e7d32",
              fontSize: { xs: "18px", sm: "20px" },
              fontWeight: "700",
              mb: 1
            }}
          >
            Current: ${backtestAccount.currentBalance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </Typography>

          <Typography 
            variant="caption" 
            sx={{ 
              color: theme[0] === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
              fontSize: "13px"
            }}
          >
            Backtesting Account
          </Typography>

          
        </CardContent>
        
        <CardActions sx={{ justifyContent: "space-between", flexWrap: "wrap", p: { xs: 2, sm: 2.5, md: 3 }, pt: 0, mt: "auto" }}>
          <Box>
            <IconButton 
              size="small" 
              onClick={() => setOpenEditDialog(true)}
              sx={{ 
                color: theme[0] === "dark" ? "#3b82f6" : "#1976d2",
                "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.1)" }
              }}
            >
              <EditIcon />
            </IconButton>
          </Box>
        </CardActions>
      </Card>

      {/* Edit Balance Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          bgcolor: theme[0] === "dark" ? "#25262b" : "#ffffff",
          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
          borderBottom: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`
        }}>
          Edit Backtest Balance
        </DialogTitle>
        <DialogContent sx={{ 
          bgcolor: theme[0] === "dark" ? "#25262b" : "#ffffff",
          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
          p: 4,
          pt: 5,
          minWidth: "400px"
        }}>
          <Box sx={{ mb: 3, mt: 2 }}>
            <CustomSingleTextField
              label="Balance ($)"
              type="number"
              value={balance}
              onChange={(e) => setBalance(parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 100 }}
              variant="outlined"
              fullWidth
            />
          </Box>
          <Typography variant="body2" sx={{ 
            color: theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
            fontSize: "14px",
            mt: 2
          }}>
            Current: ${backtestAccount.currentBalance.toFixed(2)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          bgcolor: theme[0] === "dark" ? "#25262b" : "#ffffff",
          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
          p: 3,
          borderTop: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`
        }}>
          <Button onClick={() => setOpenEditDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleBalanceUpdate} 
            variant="contained"
            disabled={isUpdating}
            sx={{ 
              backgroundColor: "#3b82f6",
              "&:hover": { backgroundColor: "#2563eb" },
              "&:disabled": { backgroundColor: "#9ca3af" }
            }}
          >
            {isUpdating ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BacktestAccountCard;
